import { useCallback, useEffect, useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackClickCheckAddress } from '~/analytics/eventTracker';
import { captureUXEvent } from '~/analytics/productAnalytics';
import { Route_InternetOfferSubPage } from '../InternetOfferSubPage';
import { SearchResponse, ispAddressLookup, ispAddressRefine } from '../internet.api';
import { InternetCustomerAddress, useInternetFlowStore } from '../useInternetStore';

export const useAddressSearch = () => {
  const navigate = useNavigate();
  const selectedAddress = useInternetFlowStore((s) => s.selectedAddress);
  const setSelectedAddress = useInternetFlowStore((s) => s.setSelectedAddress);
  const [searchString, setSearchString] = useState('');
  const [debouncedText, setDebouncedText] = useState('');
  const [data, setData] = useState<SearchResponse | null>(null);

  const [isPending, transition] = useTransition();

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedText(searchString);
    }, 450);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchString]);

  useEffect(() => {
    if (debouncedText.length > 4) {
      transition(async () => {
        setData(await ispAddressLookup(debouncedText));
      });
    } else {
      setData(null);
    }
  }, [debouncedText]);

  useEffect(() => {
    if (selectedAddress) {
      setData(null);
    }
  }, [selectedAddress]);

  /**
   * Pass false as a second argument to perform search immediately without debounce.
   */
  const exportedSetSearchString = useCallback(
    (value: string | InternetCustomerAddress) => {
      if (typeof value === 'string') {
        setSearchString(value);
        setErrorMessage(undefined);
        // if the difference in length is, say, more than 6 charachters assume autocomplete and update debouncedText immediately
        if (value.length - debouncedText.length > 6) {
          setDebouncedText(value);
        }
      } else {
        transition(async () => {
          setData(await ispAddressRefine(value));
        });
      }
    },
    [debouncedText],
  );
  const onSelectAutocompleteSuggestion = useCallback(
    (addr: InternetCustomerAddress) => {
      if (addr.count === 1 && addr.addrType === 'single') {
        setSelectedAddress(addr);
        setData(null);
      } else {
        exportedSetSearchString(addr);
      }
      const input = document.getElementById('s-input') as HTMLInputElement | null;
      if (input) {
        input.value = addr.description;
      }
      captureUXEvent('rtv_address_suggestion_selected', { addressDescription: addr.description, addressCount: addr.count });
    },
    [setSelectedAddress, exportedSetSearchString],
  );
  const [errorMessage, setErrorMessage] = useState<string>()
  const onClickCheckAvailability = useCallback(() => {
    if (selectedAddress) {
      trackClickCheckAddress({ city: selectedAddress.city, postal_code: selectedAddress.postalCode });
      navigate(Route_InternetOfferSubPage);
    } else {
      setErrorMessage('Søk på adressen der du ønsker å ha internett');
    }
  }, [selectedAddress, navigate]);

  return {
    data,
    isLoading: isPending,
    errorMessage,
    setSearchString: exportedSetSearchString,
    selectedAddress,
    onSelectAutocompleteSuggestion,
    onClickCheckAvailability,
  };
};
